import type { AnnotationHistoryDTO } from "@/features/korrektur/korrektur.types";
import type { KorrekturStepDTO } from "@/features/dashboard/korrekturOverview.types";
import { BaseApiClient } from "../api/baseApiClient.service";
import { hashContent } from "../utils";

export interface KorrekturStepAnnotationsDTO {
	korrekturStudentKlausurId: string;
	korrekturStep: Pick<
		KorrekturStepDTO,
		"id" | "locked" | "status" | "stepNr" | "color"
	>;
	/**
	 * Convenience: all latest-per-page annotations flattened into one InstantJSON.
	 */
	annotations: InstantJSON;
	/**
	 * Latest annotation state per page (most recent PageAnnotation per pageIndex).
	 */
	pages: Array<{
		pageIndex: number;
		annotations: InstantJSON;
	}>;
}

/**
 * Service for handling annotation-related operations, getting the annotations for a given document or uploading new annotations
 */
export class AnnotationService {
	private static instance: AnnotationService;

	private baseApi: BaseApiClient;

	private baseApiSubPath = "/annotations";

	private constructor() {
		this.baseApi = BaseApiClient.getInstance();
	}

	public static getInstance(): AnnotationService {
		if (!AnnotationService.instance) {
			AnnotationService.instance = new AnnotationService();
		}
		return AnnotationService.instance;
	}

	/**
	 * Get the current PDF annotations for a korrekturStudentKlausur
	 * @param korrekturStudentKlausurId - The id of the korrektur student klausur
	 * @returns The current annotations as InstantJSON or null if none exist
	 */
	public async getPdfAnnotationsForKorrekturStudentKlausur(
		korrekturStudentKlausurId: string,
	): Promise<InstantJSON | null> {
		if (!korrekturStudentKlausurId) {
			return Promise.reject(
				new Error("Korrektur Student Klausur ID is required"),
			);
		}

		const annotations = await this.baseApi.get<InstantJSON | null>(
			`${this.baseApiSubPath}/${korrekturStudentKlausurId}/annotations`,
		);

		return annotations;
	}

	/**
	 * Get the annotation history for a korrekturStudentKlausur
	 * @param korrekturStudentKlausurId - The id of the korrektur student klausur
	 * @returns The annotation history
	 */
	public async getAnnotationHistory(
		korrekturStudentKlausurId: string,
	): Promise<AnnotationHistoryDTO[]> {
		if (!korrekturStudentKlausurId) {
			return Promise.reject(
				new Error("Korrektur Student Klausur ID is required"),
			);
		}

		const annotationHistory: AnnotationHistoryDTO[] = await this.baseApi.get<
			AnnotationHistoryDTO[]
		>(
			`${this.baseApiSubPath}/${korrekturStudentKlausurId}/get-annotation-history`,
		);

		return annotationHistory;
	}

	/**
	 * Get annotations from other korrektur steps (other korrigierende) for the same student klausur.
	 * @param korrekturStudentKlausurId - The id of the korrektur student klausur
	 */
	public async getAnnotationsFromOtherKorrekturSteps(
		korrekturStudentKlausurId: string,
	): Promise<KorrekturStepAnnotationsDTO[]> {
		if (!korrekturStudentKlausurId) {
			return Promise.reject(
				new Error("Korrektur Student Klausur ID is required"),
			);
		}

		const otherStepAnnotations = await this.baseApi.get<
			KorrekturStepAnnotationsDTO[]
		>(
			`${this.baseApiSubPath}/${korrekturStudentKlausurId}/get-annotations-from-other-korrektur-steps`,
		);

		return otherStepAnnotations;
	}

	/**
	 * Update PDF annotations for a korrekturStudentKlausur
	 * @param korrekturStepId - The id of the korrektur step
	 * @param korrekturStudentKlausurId - The id of the korrektur student klausur
	 * @param annotations - The annotations to update
	 * @param pageIndex - The page index to update
	 *
	 * THIS WILL GENERATE A CHECKSUM ON THE FLY WHICH WILL BE USED TO VALIDATE THE ANNOTATIONS SERVER SIDE
	 */
	public async updateUserPdfAnnotations(
		korrekturStepId: string,
		korrekturStudentKlausurId: string,
		annotations: InstantJSON,
		pageIndex: number,
	): Promise<void> {
		const checksum = (
			await hashContent(JSON.stringify(annotations) + pageIndex)
		).slice(0, 10);
		await this.baseApi.put(
			`${this.baseApiSubPath}/${korrekturStepId}/korrektur-student-klausur/${korrekturStudentKlausurId}/update-user-pdf-annotations?pageIndex=${pageIndex}`,
			{
				annotations,
				checksum,
			},
		);
	}
}
